import _ from 'lodash';

const DUPLICATE_ROW_PREFIX = /^Row\s+(\d+)\s*:\s*/i;
const VALIDATION_ROW_PREFIX = /^Row\s+(\d+)\s*:\s*/i;

export function parseDuplicateEntry(entry) {
  if (!entry || typeof entry !== 'string') {
    return null;
  }

  const trimmed = entry.trim();
  const rowMatch = DUPLICATE_ROW_PREFIX.exec(trimmed);
  if (!rowMatch) {
    return { row: null, name: trimmed, identifier: '' };
  }

  const rest = trimmed.slice(rowMatch[0].length);
  const lastSeparator = rest.lastIndexOf(' : ');
  if (lastSeparator === -1) {
    return {
      row: Number(rowMatch[1]),
      name: rest.trim(),
      identifier: ''
    };
  }

  return {
    row: Number(rowMatch[1]),
    name: rest.slice(0, lastSeparator).trim(),
    identifier: rest.slice(lastSeparator + 3).trim()
  };
}

export function normalizeDuplicateErrors(duplicates = []) {
  const seen = new Set();
  const items = [];

  duplicates.forEach(entry => {
    const parsed = parseDuplicateEntry(entry);
    if (!parsed) {
      return;
    }

    const key = `${parsed.row}|${parsed.name}|${parsed.identifier}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    items.push(parsed);
  });

  return items.sort((a, b) => (a.row ?? 0) - (b.row ?? 0));
}

export function groupDuplicatesByRow(duplicates = []) {
  return Object.entries(_.groupBy(normalizeDuplicateErrors(duplicates), item => item.row ?? 'unknown'))
    .map(([row, records]) => ({
      row: row === 'unknown' ? null : Number(row),
      records
    }))
    .sort((a, b) => (a.row ?? 0) - (b.row ?? 0));
}

function getValidationRowNumber(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const row = item.rowNumber ?? item.row ?? item.RowNumber ?? item.Row;
  if (row === null || row === undefined || row === '') {
    return null;
  }

  const parsed = Number(row);
  return Number.isFinite(parsed) ? parsed : null;
}

function getRowLevelMessage(item) {
  if (typeof item.message === 'string' && item.message.trim()) {
    return item.message.trim();
  }

  if (typeof item.errorMessage === 'string' && item.errorMessage.trim()) {
    return item.errorMessage.trim();
  }

  return null;
}

function getMessageFromValidationResult(result, rowMessage) {
  const resultMessage = result?.errorMessage || result?.message || result?.ErrorMessage;
  if (typeof resultMessage === 'string' && resultMessage.trim()) {
    return resultMessage.trim();
  }

  return rowMessage;
}

function getValidationMessagesFromObject(item) {
  const rowMessage = getRowLevelMessage(item);
  const { validationResults, errors } = item;

  if (Array.isArray(validationResults) && validationResults.length > 0) {
    return validationResults
      .map(result => getMessageFromValidationResult(result, rowMessage))
      .filter(Boolean);
  }

  if (rowMessage) {
    return [rowMessage];
  }

  if (Array.isArray(errors)) {
    const errorMessages = errors
      .map(error => {
        if (typeof error === 'string' && error.trim()) {
          return error.trim();
        }

        return getMessageFromValidationResult(error, rowMessage);
      })
      .filter(Boolean);

    if (errorMessages.length) {
      return errorMessages;
    }
  }

  return [];
}

export function parseValidationEntry(entry) {
  if (typeof entry === 'string') {
    const trimmed = entry.trim();
    const rowMatch = VALIDATION_ROW_PREFIX.exec(trimmed);
    if (rowMatch) {
      return {
        row: Number(rowMatch[1]),
        messages: [trimmed.slice(rowMatch[0].length).trim()].filter(Boolean)
      };
    }

    return {
      row: null,
      messages: [trimmed].filter(Boolean)
    };
  }

  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const messages = getValidationMessagesFromObject(entry);
  if (!messages.length) {
    return null;
  }

  return {
    row: getValidationRowNumber(entry),
    messages
  };
}

export function formatValidationErrors(validationErrors = []) {
  const byRow = {};

  validationErrors.forEach(entry => {
    const parsed = parseValidationEntry(entry);
    if (!parsed?.messages?.length) {
      return;
    }

    const rowKey = parsed.row ?? 'unknown';
    if (!byRow[rowKey]) {
      byRow[rowKey] = [];
    }

    byRow[rowKey].push(...parsed.messages);
  });

  return Object.entries(byRow)
    .map(([row, messages]) => ({
      row: row === 'unknown' ? null : Number(row),
      messages: _.uniq(messages)
    }))
    .sort((a, b) => (a.row ?? 0) - (b.row ?? 0));
}

export function parseImportErrorPayload(err) {
  const payload = err && typeof err === 'object' ? err : {};
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const duplicates = Array.isArray(nested.duplicates)
    ? nested.duplicates
    : Array.isArray(payload.duplicates)
      ? payload.duplicates
      : [];
  const validationErrors = Array.isArray(nested.validationErrors)
    ? nested.validationErrors
    : Array.isArray(payload.validationErrors)
      ? payload.validationErrors
      : Array.isArray(nested.errors)
        ? nested.errors
        : [];

  return {
    duplicates,
    validationErrors,
    duplicateGroups: groupDuplicatesByRow(duplicates),
    validationGroups: formatValidationErrors(validationErrors),
  };
}

export function getExpandedErrorPanelKeys(duplicateGroups, validationGroups) {
  const keys = [];

  if (duplicateGroups.length > 0) {
    keys.push('duplicates');
  }

  if (validationGroups.length > 0) {
    keys.push('validation');
  }

  return keys;
}
